import React, { useCallback, useMemo, useState } from 'react'
import { type CustomIntegration, type CustomIntegrationAuthType } from '../../../models/CustomIntegration'
import { useUpdateCustomIntegrationMutation } from '../../../store/customIntegration/customIntegration'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'
import { AuthTypeSelector } from '../components/AuthTypeSelector'
import { WizardStepHeader } from '../components/WizardStepCard'
import { AuthDetailsFormFields } from '../components/AuthDetailsFormFields'
import { AUTH_DETAILS_SUBTITLE } from '../form/authTypeOptions'
import { FormSaveBar } from '../../../components/FormSaveBar/FormSaveBar'
import {
  type AuthFormState,
  authFormStateFromConfig,
  buildAuthPatch,
  hasAuthDetailsStep,
  isAuthEditValid,
  isSameFormValue,
} from '../form/customIntegrationForm'
import { useUnsavedChangesGuard } from '../../../common/useUnsavedChangesGuard'
import { authChangeInvalidatesCredentials } from '../form/credentialInvalidation'
import { userApi } from '../../../store/user/user'
import { DisconnectUsersWarningModal } from './DisconnectUsersWarningModal'

interface CustomIntegrationAuthenticationTabProps {
  customIntegration: CustomIntegration
  readOnly: boolean
}

export const CustomIntegrationAuthenticationTab: React.FC<CustomIntegrationAuthenticationTabProps> = ({ customIntegration, readOnly }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [updateCustomIntegration, { isLoading: isSaving }] = useUpdateCustomIntegrationMutation()

  const initialAuthType = customIntegration.auth.type
  const initialForms = useMemo(() => authFormStateFromConfig(customIntegration.auth), [customIntegration.auth])
  const initialClientId = customIntegration.auth.type === 'oauth2' ? customIntegration.auth.clientId : ''

  const [authType, setAuthType] = useState<CustomIntegrationAuthType>(initialAuthType)
  const [authForms, setAuthForms] = useState<AuthFormState>(initialForms)
  const [showDisconnectWarning, setShowDisconnectWarning] = useState<boolean>(false)

  const typeChanged = authType !== initialAuthType
  const isDirty = typeChanged || !isSameFormValue(authForms, initialForms)
  const disconnectsUsers = authChangeInvalidatesCredentials(initialAuthType, initialForms, authType, authForms)
  const isValid = isAuthEditValid(authType, authForms, initialAuthType, initialClientId)
  const showDetails = !readOnly && hasAuthDetailsStep(authType)
  const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesGuard(isDirty && !readOnly, isSaving)

  const handleDiscard = useCallback(() => {
    setAuthType(initialAuthType)
    setAuthForms(initialForms)
  }, [initialAuthType, initialForms])

  const save = useCallback(async () => {
    try {
      await updateCustomIntegration({ accountId, customIntegrationId: customIntegration.id, payload: { auth: buildAuthPatch(authType, authForms) } }).unwrap()
      if (disconnectsUsers) {
        dispatch(userApi.util.invalidateTags(['me']))
      }
      dispatch(
        addToast({
          variant: 'success',
          title: 'Authentication settings saved.',
          messages: disconnectsUsers ? ['Every user needs to connect again.'] : undefined,
        }),
      )
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to save the authentication settings. Please try again.'] }))
    } finally {
      setShowDisconnectWarning(false)
    }
  }, [updateCustomIntegration, accountId, customIntegration.id, authType, authForms, disconnectsUsers, dispatch])

  const handleSave = useCallback(async () => {
    if (disconnectsUsers) {
      setShowDisconnectWarning(true)
      return
    }
    await save()
  }, [disconnectsUsers, save])

  return (
    <div className='d-flex flex-column min-vh-100'>
      <div className='d-flex flex-column flex-grow-1 zocks-gap-2 zocks-pb-10.0'>
        <section className='d-flex flex-column gap-4' aria-label='Authorization type'>
          <WizardStepHeader icon='bi-shield-lock' title='Authorization type' />
          <div className='px-5'>
            <AuthTypeSelector value={authType} onChange={setAuthType} readOnly={readOnly} layout='horizontal' showHint={false} />
          </div>
        </section>

        {showDetails ? (
          <section className='d-flex flex-column gap-4' aria-label='Authorization Details'>
            <WizardStepHeader icon='bi-journal-text' title='Authorization Details' subtitle={AUTH_DETAILS_SUBTITLE} />
            <div className='px-5'>
              <AuthDetailsFormFields
                authType={authType}
                state={authForms}
                onChange={setAuthForms}
                disabled={readOnly}
                hasStoredSecret={authType === 'oauth2' && initialAuthType === 'oauth2' && authForms.oauth2.clientId.trim() === initialClientId}
              />
            </div>
          </section>
        ) : null}
      </div>

      <DisconnectUsersWarningModal
        show={showDisconnectWarning}
        name={customIntegration.name}
        isSaving={isSaving}
        onHide={() => setShowDisconnectWarning(false)}
        onConfirm={save}
      />
      {readOnly ? null : (
        <FormSaveBar
          isVisible={isDirty}
          isDirty={isDirty}
          isValid={isValid}
          isSubmitting={isSaving}
          onDiscard={handleDiscard}
          onSave={handleSave}
          discardLabel='Discard changes'
          saveLabel='Save changes'
          showNavigationWarning={isBlocked}
          onConfirmLeave={confirmLeave}
          onCancelLeave={cancelLeave}
        />
      )}
    </div>
  )
}
